/* eslint-disable react-hooks/set-state-in-effect */
import { useEffect, useState, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/api';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import {
  BookOpen, Users, Settings, Plus, Pencil, Trash2, Eye, Loader2, Save, Globe, EyeOff, Calendar
} from 'lucide-react';

const TABS = [
  { id: 'books', label: 'Livros', icon: BookOpen },
  { id: 'authors', label: 'Autores', icon: Users },
  { id: 'settings', label: 'Definições', icon: Settings },
];

const BOOK_STATUSES = [
  { id: 'draft', label: 'Rascunho' },
  { id: 'in_review', label: 'Em revisão' },
  { id: 'scheduled', label: 'Agendado' },
  { id: 'published', label: 'Publicado' },
  { id: 'hidden', label: 'Oculto' },
];

const AUTHOR_STATUSES = [
  { id: 'draft', label: 'Rascunho' },
  { id: 'published', label: 'Publicado' },
  { id: 'hidden', label: 'Oculto' },
];

const EMPTY_BOOK = {
  title: '', author_id: '', author_name: '', cover_image: '',
  short_description: '', synopsis: '', genres: [], pages: null,
  publisher: '', formats: [], publication_date: '',
  contact_method: 'external',
  purchase_url: '', purchase_button_label: 'Comprar este livro',
  indicative_price: '',
  recipient_email: '', recipient_email_secondary: '',
  send_copy_to_intimate: true, send_confirmation_to_reader: true,
  form_button_label: 'Pedir este livro',
  excerpt: '', gallery: [], featured: false, is_new_release: false,
  status: 'draft', scheduled_at: '',
};

const EMPTY_AUTHOR = { name: '', photo: '', biography: '', website: '', social: {}, status: 'draft' };

export default function AdminLivros() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('books');
  const [loading, setLoading] = useState(true);
  const [books, setBooks] = useState([]);
  const [authors, setAuthors] = useState([]);
  const [sectionStatus, setSectionStatus] = useState('preparation');
  const [editingBook, setEditingBook] = useState(null);
  const [editingAuthor, setEditingAuthor] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [b, a, s] = await Promise.all([
        api.get('/admin/livros/books'),
        api.get('/admin/livros/authors'),
        api.get('/admin/livros/section-status'),
      ]);
      setBooks(b.data);
      setAuthors(a.data);
      setSectionStatus(s.data.status);
    } catch {
      toast.error('Erro a carregar dados');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!user?.is_admin) { navigate('/home'); return; }
    loadAll();
  }, [user, navigate, loadAll]);

  const toggleSection = async (checked) => {
    const newStatus = checked ? 'published' : 'preparation';
    try {
      await api.put('/admin/livros/section-status', { status: newStatus });
      setSectionStatus(newStatus);
      toast.success(checked ? 'Livros PUBLICADA ao público' : 'Livros em preparação (privada)');
    } catch {
      toast.error('Erro a alterar');
    }
  };

  const saveBook = async () => {
    setSaving(true);
    try {
      const payload = {
        ...editingBook,
        pages: editingBook.pages ? parseInt(editingBook.pages, 10) : null,
        author_id: editingBook.author_id || null,
        scheduled_at: editingBook.status === 'scheduled' ? editingBook.scheduled_at : null,
        genres: Array.isArray(editingBook.genres) ? editingBook.genres : (editingBook.genres || '').split(',').map(x=>x.trim()).filter(Boolean),
        formats: Array.isArray(editingBook.formats) ? editingBook.formats : (editingBook.formats || '').split(',').map(x=>x.trim()).filter(Boolean),
        gallery: Array.isArray(editingBook.gallery) ? editingBook.gallery : (editingBook.gallery || '').split('\n').map(x=>x.trim()).filter(Boolean),
      };
      if (editingBook.id) {
        await api.put(`/admin/livros/books/${editingBook.id}`, payload);
      } else {
        await api.post('/admin/livros/books', payload);
      }
      toast.success('Livro guardado');
      setEditingBook(null);
      loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro a guardar');
    } finally { setSaving(false); }
  };

  const deleteBook = async (b) => {
    if (!confirm(`Eliminar "${b.title}"?`)) return;
    try { await api.delete(`/admin/livros/books/${b.id}`); toast.success('Eliminado'); loadAll(); }
    catch { toast.error('Erro a eliminar'); }
  };

  const saveAuthor = async () => {
    setSaving(true);
    try {
      const payload = {
        ...editingAuthor,
        social: typeof editingAuthor.social === 'object' ? editingAuthor.social : {},
      };
      if (editingAuthor.id) await api.put(`/admin/livros/authors/${editingAuthor.id}`, payload);
      else await api.post('/admin/livros/authors', payload);
      toast.success('Autor guardado');
      setEditingAuthor(null);
      loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro a guardar');
    } finally { setSaving(false); }
  };

  const deleteAuthor = async (a) => {
    if (!confirm(`Eliminar "${a.name}"? Os livros ficam sem autor (não são apagados).`)) return;
    try { await api.delete(`/admin/livros/authors/${a.id}`); toast.success('Eliminado'); loadAll(); }
    catch { toast.error('Erro a eliminar'); }
  };

  if (loading) return <div className="min-h-screen bg-background"><Navbar /><div className="flex items-center justify-center h-96"><Loader2 className="w-8 h-8 animate-spin text-ruby" /></div></div>;

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="max-w-6xl mx-auto px-4 pt-24 pb-12">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <BookOpen className="w-7 h-7 text-ruby" />
            <div>
              <h1 className="text-3xl font-bold text-foreground">Livros</h1>
              <p className="text-muted-foreground text-sm">Gere livros e autores em privado. Publica a secção quando estiver pronta.</p>
            </div>
          </div>
          <div className="flex items-center gap-3 bg-surface border border-border px-3 py-2 rounded-md">
            {sectionStatus === 'published' ? <Globe className="w-4 h-4 text-emerald-400" /> : <EyeOff className="w-4 h-4 text-amber-400" />}
            <span className="text-sm text-foreground">{sectionStatus === 'published' ? 'Secção PÚBLICA' : 'Em preparação'}</span>
            <Switch
              checked={sectionStatus === 'published'}
              onCheckedChange={toggleSection}
              data-testid="section-status-toggle"
            />
          </div>
        </div>

        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {TABS.map(t => (
            <Button key={t.id} variant={tab === t.id ? 'default' : 'outline'} onClick={() => setTab(t.id)} className={tab === t.id ? 'bg-ruby' : 'border-border'} data-testid={`livros-tab-${t.id}`}>
              <t.icon className="w-4 h-4 mr-2" />{t.label}
            </Button>
          ))}
        </div>

        {tab === 'books' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">{books.length} livro(s)</p>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => window.open('/admin/livros/preview/catalogue', '_blank')} data-testid="preview-catalogue">
                  <Eye className="w-4 h-4 mr-2" />Pré-visualizar catálogo
                </Button>
                <Button onClick={() => setEditingBook({ ...EMPTY_BOOK })} className="bg-ruby" data-testid="new-book">
                  <Plus className="w-4 h-4 mr-2" />Novo Livro
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {books.map(b => (
                <Card key={b.id} className="bg-surface border-border" data-testid={`book-row-${b.id}`}>
                  <CardContent className="p-3 flex gap-3">
                    <div className="w-16 h-24 rounded bg-white/5 overflow-hidden shrink-0">
                      {b.cover_image ? <img src={b.cover_image} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><BookOpen className="w-5 h-5 text-muted-foreground" /></div>}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-foreground text-sm truncate">{b.title}</p>
                      <p className="text-xs text-muted-foreground truncate">{b.author_display_name || '—'}</p>
                      <Badge variant="outline" className={`mt-1 text-[10px] border-border ${
                        b.status === 'published' ? 'text-emerald-400' :
                        b.status === 'scheduled' ? 'text-blue-400' :
                        b.status === 'hidden' ? 'text-muted-foreground' :
                        'text-amber-400'
                      }`}>
                        {BOOK_STATUSES.find(s => s.id === b.status)?.label || b.status}
                      </Badge>
                      <div className="flex gap-1 mt-2">
                        <Button size="sm" variant="outline" onClick={() => setEditingBook({ ...EMPTY_BOOK, ...b, genres: (b.genres||[]).join(', '), formats:(b.formats||[]).join(', '), gallery:(b.gallery||[]).join('\n') })} data-testid={`edit-book-${b.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                        <Button size="sm" variant="outline" onClick={() => window.open(`/admin/livros/preview/book/${b.id}`, '_blank')} data-testid={`preview-book-${b.id}`}><Eye className="w-3.5 h-3.5" /></Button>
                        <Button size="sm" variant="outline" onClick={() => deleteBook(b)} className="text-red-400 border-red-500/30" data-testid={`delete-book-${b.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
              {books.length === 0 && <p className="text-muted-foreground py-8">Ainda não há livros. Cria o primeiro.</p>}
            </div>
          </div>
        )}

        {tab === 'authors' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">{authors.length} autor(es)</p>
              <Button onClick={() => setEditingAuthor({ ...EMPTY_AUTHOR })} className="bg-ruby" data-testid="new-author">
                <Plus className="w-4 h-4 mr-2" />Novo Autor
              </Button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {authors.map(a => (
                <Card key={a.id} className="bg-surface border-border" data-testid={`author-row-${a.id}`}>
                  <CardContent className="p-3 flex gap-3 items-start">
                    <div className="w-14 h-14 rounded-full bg-white/5 overflow-hidden shrink-0">
                      {a.photo ? <img src={a.photo} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><Users className="w-5 h-5 text-muted-foreground" /></div>}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-foreground text-sm truncate">{a.name}</p>
                      <p className="text-xs text-muted-foreground">{a.books_count || 0} livro(s)</p>
                      <Badge variant="outline" className={`mt-1 text-[10px] border-border ${a.status === 'published' ? 'text-emerald-400' : a.status === 'hidden' ? 'text-muted-foreground' : 'text-amber-400'}`}>
                        {AUTHOR_STATUSES.find(s => s.id === a.status)?.label}
                      </Badge>
                      <div className="flex gap-1 mt-2">
                        <Button size="sm" variant="outline" onClick={() => setEditingAuthor({ ...a })} data-testid={`edit-author-${a.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                        <Button size="sm" variant="outline" onClick={() => window.open(`/admin/livros/preview/author/${a.id}`, '_blank')} data-testid={`preview-author-${a.id}`}><Eye className="w-3.5 h-3.5" /></Button>
                        <Button size="sm" variant="outline" onClick={() => deleteAuthor(a)} className="text-red-400 border-red-500/30" data-testid={`delete-author-${a.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
              {authors.length === 0 && <p className="text-muted-foreground py-8">Ainda não há autores.</p>}
            </div>
          </div>
        )}

        {tab === 'settings' && (
          <Card className="bg-surface border-border max-w-2xl">
            <CardHeader>
              <CardTitle className="text-foreground">Estado da secção</CardTitle>
              <CardDescription>Controla a visibilidade pública global. Enquanto está &quot;Em preparação&quot;, nada é acessível ao público — nem por URL directo.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-white/5 rounded-md">
                <div>
                  <p className="text-foreground font-semibold">Publicar Livros ao público</p>
                  <p className="text-xs text-muted-foreground">Atualiza instantaneamente. Não precisa de redeploy.</p>
                </div>
                <Switch checked={sectionStatus === 'published'} onCheckedChange={toggleSection} />
              </div>
              <p className="text-xs text-muted-foreground">
                Estado actual: <b>{sectionStatus === 'published' ? 'PÚBLICA' : 'Em preparação'}</b>.
              </p>
            </CardContent>
          </Card>
        )}
      </main>

      {/* Book editor */}
      <Dialog open={!!editingBook} onOpenChange={(o) => !o && setEditingBook(null)}>
        <DialogContent className="bg-surface border-border max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="book-editor">
          <DialogHeader>
            <DialogTitle>{editingBook?.id ? 'Editar Livro' : 'Novo Livro'}</DialogTitle>
            <DialogDescription>Todos os campos são estruturados — sem HTML.</DialogDescription>
          </DialogHeader>
          {editingBook && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Título *"><Input value={editingBook.title} onChange={e=>setEditingBook({...editingBook, title:e.target.value})} data-testid="book-title" /></Field>
              <Field label="Autor">
                <Select value={editingBook.author_id || '__none__'} onValueChange={v => setEditingBook({...editingBook, author_id: v==='__none__' ? '' : v})}>
                  <SelectTrigger className="bg-background border-border"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— Sem autor —</SelectItem>
                    {authors.map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Autor (texto livre, se sem perfil)"><Input value={editingBook.author_name || ''} onChange={e=>setEditingBook({...editingBook, author_name:e.target.value})} /></Field>
              <Field label="Capa (URL)"><Input value={editingBook.cover_image} onChange={e=>setEditingBook({...editingBook, cover_image:e.target.value})} /></Field>
              <Field label="Descrição curta" full><Textarea rows={2} value={editingBook.short_description} onChange={e=>setEditingBook({...editingBook, short_description:e.target.value})} /></Field>
              <Field label="Sinopse completa" full><Textarea rows={5} value={editingBook.synopsis} onChange={e=>setEditingBook({...editingBook, synopsis:e.target.value})} /></Field>
              <Field label="Excerto (opcional)" full><Textarea rows={4} value={editingBook.excerpt || ''} onChange={e=>setEditingBook({...editingBook, excerpt:e.target.value})} /></Field>
              <Field label="Géneros (vírgula)"><Input value={editingBook.genres} onChange={e=>setEditingBook({...editingBook, genres:e.target.value})} placeholder="Romance, Erótico" /></Field>
              <Field label="Formatos (vírgula)"><Input value={editingBook.formats} onChange={e=>setEditingBook({...editingBook, formats:e.target.value})} placeholder="ebook, capa mole" /></Field>
              <Field label="Nº páginas"><Input type="number" value={editingBook.pages || ''} onChange={e=>setEditingBook({...editingBook, pages:e.target.value})} /></Field>
              <Field label="Editora"><Input value={editingBook.publisher || ''} onChange={e=>setEditingBook({...editingBook, publisher:e.target.value})} /></Field>
              <Field label="Data de publicação"><Input type="date" value={(editingBook.publication_date||'').slice(0,10)} onChange={e=>setEditingBook({...editingBook, publication_date:e.target.value})} /></Field>

              {/* Contact method — exactly one */}
              <Field label="Método de contacto *" full>
                <Select value={editingBook.contact_method || 'external'} onValueChange={v=>setEditingBook({...editingBook, contact_method: v})}>
                  <SelectTrigger className="bg-background border-border" data-testid="book-contact-method"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="external">Link externo de compra</SelectItem>
                    <SelectItem value="form">Formulário de pedido de encomenda</SelectItem>
                  </SelectContent>
                </Select>
              </Field>

              {editingBook.contact_method === 'external' && (
                <>
                  <Field label="URL de compra externa *"><Input value={editingBook.purchase_url || ''} onChange={e=>setEditingBook({...editingBook, purchase_url:e.target.value})} placeholder="https://…" data-testid="book-purchase-url" /></Field>
                  <Field label="Etiqueta do botão"><Input value={editingBook.purchase_button_label} onChange={e=>setEditingBook({...editingBook, purchase_button_label:e.target.value})} /></Field>
                </>
              )}
              <Field label="Preço indicativo (opcional)"><Input value={editingBook.indicative_price || ''} onChange={e=>setEditingBook({...editingBook, indicative_price:e.target.value})} placeholder="€ 12,50" data-testid="book-price" /></Field>
              {editingBook.contact_method === 'form' && (
                <>
                  <Field label="Email destinatário principal *"><Input type="email" value={editingBook.recipient_email || ''} onChange={e=>setEditingBook({...editingBook, recipient_email:e.target.value})} placeholder="autor@exemplo.pt" data-testid="book-recipient-email" /></Field>
                  <Field label="Email secundário (opcional)"><Input type="email" value={editingBook.recipient_email_secondary || ''} onChange={e=>setEditingBook({...editingBook, recipient_email_secondary:e.target.value})} /></Field>
                  <Field label="Etiqueta do botão"><Input value={editingBook.form_button_label} onChange={e=>setEditingBook({...editingBook, form_button_label:e.target.value})} /></Field>
                  <Field label="Enviar cópia à Intimate"><Switch checked={!!editingBook.send_copy_to_intimate} onCheckedChange={v=>setEditingBook({...editingBook, send_copy_to_intimate:v})} /></Field>
                  <Field label="Confirmação ao leitor"><Switch checked={!!editingBook.send_confirmation_to_reader} onCheckedChange={v=>setEditingBook({...editingBook, send_confirmation_to_reader:v})} /></Field>
                </>
              )}
              <Field label="Galeria (URLs, um por linha)" full><Textarea rows={2} value={editingBook.gallery} onChange={e=>setEditingBook({...editingBook, gallery:e.target.value})} /></Field>
              <Field label="Estado *">
                <Select value={editingBook.status} onValueChange={v=>setEditingBook({...editingBook, status:v})}>
                  <SelectTrigger className="bg-background border-border" data-testid="book-status"><SelectValue /></SelectTrigger>
                  <SelectContent>{BOOK_STATUSES.map(s => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              {editingBook.status === 'scheduled' && (
                <Field label="Publicar a"><Input type="datetime-local" value={(editingBook.scheduled_at||'').slice(0,16)} onChange={e=>setEditingBook({...editingBook, scheduled_at:e.target.value ? new Date(e.target.value).toISOString() : ''})} /></Field>
              )}
              <Field label="Destaque"><Switch checked={!!editingBook.featured} onCheckedChange={v=>setEditingBook({...editingBook, featured:v})} /></Field>
              <Field label="Novidade"><Switch checked={!!editingBook.is_new_release} onCheckedChange={v=>setEditingBook({...editingBook, is_new_release:v})} /></Field>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingBook(null)}>Cancelar</Button>
            <Button onClick={saveBook} disabled={saving || !editingBook?.title} className="bg-ruby" data-testid="save-book">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}<Save className="w-4 h-4 mr-2" />Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Author editor */}
      <Dialog open={!!editingAuthor} onOpenChange={(o) => !o && setEditingAuthor(null)}>
        <DialogContent className="bg-surface border-border max-w-lg max-h-[90vh] overflow-y-auto" data-testid="author-editor">
          <DialogHeader>
            <DialogTitle>{editingAuthor?.id ? 'Editar Autor' : 'Novo Autor'}</DialogTitle>
            <DialogDescription>Perfil de autor.</DialogDescription>
          </DialogHeader>
          {editingAuthor && (
            <div className="space-y-3">
              <Field label="Nome *"><Input value={editingAuthor.name} onChange={e=>setEditingAuthor({...editingAuthor, name:e.target.value})} data-testid="author-name" /></Field>
              <Field label="Foto (URL)"><Input value={editingAuthor.photo || ''} onChange={e=>setEditingAuthor({...editingAuthor, photo:e.target.value})} /></Field>
              <Field label="Biografia"><Textarea rows={5} value={editingAuthor.biography || ''} onChange={e=>setEditingAuthor({...editingAuthor, biography:e.target.value})} /></Field>
              <Field label="Website"><Input value={editingAuthor.website || ''} onChange={e=>setEditingAuthor({...editingAuthor, website:e.target.value})} placeholder="https://…" /></Field>
              <Field label="Instagram"><Input value={editingAuthor.social?.instagram || ''} onChange={e=>setEditingAuthor({...editingAuthor, social:{...(editingAuthor.social||{}), instagram:e.target.value}})} /></Field>
              <Field label="Facebook"><Input value={editingAuthor.social?.facebook || ''} onChange={e=>setEditingAuthor({...editingAuthor, social:{...(editingAuthor.social||{}), facebook:e.target.value}})} /></Field>
              <Field label="Estado *">
                <Select value={editingAuthor.status} onValueChange={v=>setEditingAuthor({...editingAuthor, status:v})}>
                  <SelectTrigger className="bg-background border-border" data-testid="author-status"><SelectValue /></SelectTrigger>
                  <SelectContent>{AUTHOR_STATUSES.map(s => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingAuthor(null)}>Cancelar</Button>
            <Button onClick={saveAuthor} disabled={saving || !editingAuthor?.name} className="bg-ruby" data-testid="save-author">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}<Save className="w-4 h-4 mr-2" />Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, children, full }) {
  return (
    <div className={full ? 'md:col-span-2' : ''}>
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
